import {
	IsBoolean,
	IsEmail,
	IsNotEmpty,
	IsOptional,
	IsPhoneNumber,
	IsString,
	IsUUID,
	MinLength,
	ValidateIf,
} from 'class-validator';
import { IntakeFormReservedFieldNames } from '../../enums';

/**
 * Record<IntakeFormSectionField.name, IntakeFormValue> - key is always the NAME field of IntakeFormSectionField
 * IntakeFormUserDataResults must be a plain object with the next possible value:
 *
 * @example
 *
 * {
 * 	   "Some Text Input Name": "Some Text Input Result ", // value is raw text
 * 	   "Some Check Box Name": true, // value is boolean
 * 	   "Some Radio-Button Name": "Some Radio-Button Option Text", // value is raw text value of chosen option
 * 	   "Some Date-Picker Name": "2024-01-01", // value is d/M/yyyy
 * 	   "Some Drop-Down Name": "Some Drop-Down Option Text", // value is raw text value of chosen option
 * 	   "Some Check Box List Name": ["Chekbox 1", "Checkbox 3"] // value is array of raw text values of chosen options
 * 	}
 *
 * 	Raw values are essential for field types with option lists because IDs can be unstable; they may be deleted, changed, or otherwise altered.
 */
export type IntakeFormValue = string | number | boolean | string [] | Date | null;

export class IntakeFormUserDataResults implements Partial<Record<IntakeFormReservedFieldNames, string>> {
	[key: string]: IntakeFormValue | undefined;

	@IsOptional()
	@IsNotEmpty({ message: 'Full Name is required' })
	@MinLength(3, { message: 'Full Name is too short' })
	public reserved_fullname?: string;

	@ValidateIf(o => !o.reserved_phone || !!o.reserved_email)
	@IsOptional()
	@IsNotEmpty({ message: 'Email is required' })
	@IsEmail(undefined, { message: 'Email is invalid' })
	public reserved_email?: string;

	@ValidateIf(o => !o.reserved_email || !!o.reserved_phone)
	@IsOptional()
	@IsNotEmpty({ message: 'Phone is required' })
	@IsPhoneNumber(undefined, { message: 'Phone is invalid' })
	public reserved_phone?: string;

	@IsOptional()
	@IsUUID()
	public userId?: string;

	@IsOptional()
	@IsBoolean()
	public waiverAgreement?: boolean;

	@IsOptional()
	@IsString()
	@IsNotEmpty({ message: 'Please enter your initials' })
	public participantInitials?: string;

	/**
	 * skipSigningForm:
	 *  - TRUE means the form is not signed - event_tickets.signedAt will be set to NULL, an email will be triggered
	 *  - FALSE means the form is signed - event_tickets.signedAt will be set to the current date
	 * */
	@IsOptional()
	@IsBoolean()
	public skipSigningForm?: boolean;
}

export type IntakeFormOtherKeys = Exclude<
  keyof IntakeFormUserDataResults,
  `${IntakeFormReservedFieldNames}`
>;

export type IntakeFormOtherFields = {
  [K in IntakeFormOtherKeys]: IntakeFormUserDataResults[K];
};


export type IntakeFormSectionField = {
	// name = field ID - this is not used on a UI, e.g. 'firstName', 'email' etc. Should be used to submit values
	// this ID must be permanent even after editing the field
	// UUID value is preferable (don't use generated string from the label since this can be changed)
	name: string;
	type:
		| 'text-input' // usual text inputs, value is raw text
		| 'check-box' // value is boolean
		| 'radio-buttons' // value is text value of chosen option
		| 'date-picker' // value is d/M/yyyy
		| 'drop-down' // value is text value of chosen option
		| 'checkbox-list' // value is text value of chosen options
		| 'html'; // no value
	reserved?: boolean; // reserved system field. Don't need to show the field on the form when the value is TRUE
	isCommonForAllTickets?: boolean;
	htmlContent?: string; // HTML content for field with type = 'html'
	label?: string; // label above the field
	description?: string; // additional description under main label and above the field
	placeholder?: string; // placeholders for text-inputs
	required?: boolean; // required boolean flag
	defaultValue?: string; // default value, e.g. 'some-radio-button-id', 'some-dropdown-id', 'some initial text'
	checkboxLabel?: string; // checkbox label
	textInputType?: // for changing input behavior and validations
		'text' | 'email' | 'phone-number' | 'number';
	validateIsEqualToField?: string; // can be used for validation confirmation fields, e.g. "Confirm Email"
	tierIds?: Array<string>; // array of 'exiting-price-id' or 'new-price-index-number'. The price index will be replaced with the price ID on the backend side.
	options?: Array<{
		// for drop-downs and radio-buttons
		id: string; // some permanent ID - don't show on a UI, it must be a random UUID value. ID must be the same when option is edited
		name: string; // some label for the option, pic. 8
	}>;
};

export type IntakeFormSection = {
	sectionName?: string; // name of the section
	fields?: Array<IntakeFormSectionField>;
};

export type IntakeFormReservedSectionField = { label: string; required: boolean; enabled: true } | { enabled: false };

export type ReservedFieldPropNames = 'emailReservedField' | 'fullNameReservedField' | 'phoneReservedField';

export type EventIntakeFormSettings = {
	waiverHtml?: string; // waiver HTML that should be rendered after the form
	waiverText?: string | null; // field to save simple text for waiver. This is automatically synced to waiverHtml
	emailReservedField?: IntakeFormReservedSectionField;
	fullNameReservedField?: IntakeFormReservedSectionField;
	phoneReservedField?: IntakeFormReservedSectionField;
	sections?: Array<IntakeFormSection>;
	waiverSections?: Array<IntakeFormSection>;
	showSectionsOnceForMultipleTickets?: boolean;
};

export class EventIntakeFormDto {
	public participantFirstName: string;
	public participantLastName: string;
	public participantEmail: string;
	public participantConfirmEmail: string;
	public participantPhoneNumber: string;
	public participantTshirtSize: 'XS' | 'S' | 'M' | 'L' | 'XL' | '2XL' | '3XL';
	public emergencyContactFirstName: string;
	public emergencyContactLastName: string;
	public emergencyContactPhoneNumber: string;
	public medicalConditions: string;
	public confirmReleaseOfMedicalInformation: boolean;
	public waiverAgreement: boolean;
	public participantInitials: string;
}

export type MembershipIntakeFormSettings = {
	sections?: Array<IntakeFormSection>;
};
